import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, MapPin, Route } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { LeafletMap } from '@/components/transport/LeafletMap';
import { transportApi } from '@/services/transportApi';

const EVENT_COLORS = {
  ready: '#0EA5E9', start: '#16A34A', arrived: '#F59E0B', done: '#2563EB',
  delivered: '#0D9488', end: '#DC2626', unloading: '#C026D3',
};
const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const fmt = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true }); } catch { return ''; }
};

const EventList = ({ events }) => (
  <ol className="grid sm:grid-cols-2 gap-x-4 gap-y-1 text-xs" data-testid="trip-map-events">
    {events.map((e, i) => (
      <li key={i} className="flex items-center gap-2" data-testid={`trip-map-event-${i}`}>
        <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: EVENT_COLORS[e.kind] || '#6B7280' }} />
        <span className="font-medium">{e.label}</span>
        <span className="text-gray-500">{fmt(e.at)}</span>
        {e.lat == null && <span className="text-gray-400">(no GPS)</span>}
      </li>
    ))}
  </ol>
);

/** Trip Log "View map": the GPS route actually driven + pins for every trip event. */
export const TripMapDialog = ({ trip, onClose }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!trip) return;
    setData(null);
    transportApi.tripTrack(trip.id).then(setData).catch((e) => { toast.error(e.response?.data?.detail || 'Could not load the trip map'); onClose(); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trip]);

  const markers = useMemo(() => !data ? [] : [
    ...data.planned.map((p) => ({ lat: p.lat, lng: p.lng, color: '#9CA3AF', popup: `Planned ${esc(p.kind)}: ${esc(p.label)}` })),
    ...data.events.map((e) => ({ lat: e.lat, lng: e.lng, color: EVENT_COLORS[e.kind], popup: `<b>${esc(e.label)}</b><br/>${esc(fmt(e.at))}` })),
  ], [data]);
  const hasTrack = (data?.track?.length || 0) >= 2;

  return (
    <Dialog open={!!trip} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="trip-map-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Route className="w-5 h-5 text-teal-600" />Trip map</DialogTitle>
          <DialogDescription data-testid="trip-map-route">{trip?.route_summary || `${trip?.pickup || '—'} → ${trip?.drop || '—'}`} · {trip?.vehicle_label} · {trip?.driver_name}</DialogDescription>
        </DialogHeader>
        {!data ? <p className="text-sm text-gray-500 flex items-center py-10 justify-center"><Loader2 className="w-4 h-4 mr-2 animate-spin" />Loading map…</p> : (
          <div className="space-y-3">
            <LeafletMap markers={markers} track={hasTrack ? data.track : null} height="420px" testId="trip-map" />
            {hasTrack
              ? <p className="text-xs text-teal-700 flex items-center gap-1" data-testid="trip-map-track-info"><span className="w-4 h-1 rounded bg-teal-600 inline-block" />GPS route driven · {data.track_points} points · ~{data.track_km} km tracked (odometer is the billed distance)</p>
              : <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 flex items-center gap-1" data-testid="trip-map-no-track"><MapPin className="w-3 h-3" />No GPS route was recorded for this trip — showing the pins we have.</p>}
            {data.events.length > 0 ? <EventList events={data.events} /> : <p className="text-xs text-gray-400" data-testid="trip-map-no-events">No trip events recorded yet.</p>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TripMapDialog;
